import { Checkbox, Flex, IconButton, ProductColor, Typography } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra, aloft/prefer-sound-ui-box
import { Box } from '@aloft/toolkit-sui/chakra';
import { ColumnDef, Row } from '@tanstack/react-table';

import { ChangeEvent, Dispatch, SetStateAction } from 'react';

import { CompSelectionProperty, CompSelectionState } from '../../types.ts';
import { StopColumnDrag } from './StopColumnDrag.tsx';

const SELECT_COLUMN_SIZE = 48;
const AIV_ARV_SELECT_COLUMN_SIZE = 112;

const AivArvHeaderTag = ({
  label,
  backgroundColor,
  color,
}: {
  label: string;
  backgroundColor: ProductColor;
  color: ProductColor;
}) => (
  <Flex
    alignItems="center"
    justifyContent="center"
    padding="4px 8px"
    borderRadius="4px"
    backgroundColor={backgroundColor}
    flexShrink={0}
  >
    <Typography
      variant="body2"
      color={color}
      __dangerDoNotUseSx={{ fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap' }}
    >
      {label}
    </Typography>
  </Flex>
);

const noFocusRingSx = {
  '& .chakra-checkbox__control:focus': { boxShadow: 'none' },
  '& .chakra-checkbox__control:focus-visible': { boxShadow: 'none' },
};

export const makeSelectionColumn = (
  getRowSelection: () => CompSelectionState,
  setRowSelection: Dispatch<SetStateAction<CompSelectionState>>,
  isInspectifyOrder: boolean,
  hasArvOption: boolean,
  onHeaderButtonClick?: () => void,
  isFilterPanelOpen?: boolean,
): ColumnDef<CompSelectionProperty> => {
  const FilterButton = () => (
    <StopColumnDrag>
      <IconButton
        icon="equalizer"
        size="xs"
        variant={isFilterPanelOpen ? 'primary' : 'secondary'}
        ariaLabel={isFilterPanelOpen ? 'Close filters' : 'Open filters'}
        onClick={() => onHeaderButtonClick?.()}
      />
    </StopColumnDrag>
  );

  if (!isInspectifyOrder || !hasArvOption) {
    return {
      id: 'select',
      size: SELECT_COLUMN_SIZE,
      minSize: SELECT_COLUMN_SIZE,
      maxSize: SELECT_COLUMN_SIZE,
      header: () => (
        <Flex alignItems="center" justifyContent="center" height="100%">
          <FilterButton />
        </Flex>
      ),
      cell: ({ row }: { row: Row<CompSelectionProperty> }) => {
        if (row.id === 'subject') return null;

        const rowId = row.id;
        const rowSelection = getRowSelection();
        const isSelected = !!rowSelection[rowId];

        const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
          const { checked } = e.target;
          setRowSelection((prev) => {
            if (checked) {
              return { ...prev, [rowId]: { listingData: row.original.listingData } };
            }
            const newSelection = { ...prev };
            delete newSelection[rowId];
            return newSelection;
          });
        };

        return (
          <Flex
            height="100%"
            minWidth={`${SELECT_COLUMN_SIZE}px`}
            alignItems="center"
            justifyContent="center"
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={noFocusRingSx}>
              <Checkbox
                onChange={handleChange}
                isChecked={isSelected}
                aria-label={`Comp selection for ${rowId}`}
              />
            </Box>
          </Flex>
        );
      },
    };
  }

  return {
    id: 'select',
    size: AIV_ARV_SELECT_COLUMN_SIZE,
    minSize: AIV_ARV_SELECT_COLUMN_SIZE,
    maxSize: AIV_ARV_SELECT_COLUMN_SIZE,
    header: () => (
      <Flex alignItems="center" height="100%" width="100%" gap="8px">
        <FilterButton />
        <AivArvHeaderTag label="AIV" backgroundColor="aiv" color="aivText" />
        <AivArvHeaderTag label="ARV" backgroundColor="arv" color="arvText" />
      </Flex>
    ),
    cell: ({ row }: { row: Row<CompSelectionProperty> }) => {
      if (row.id === 'subject') return null;

      const rowId = row.id;
      const rowSelection = getRowSelection();
      const selection = rowSelection[rowId] || {};
      const isAivSelected = selection.aiv ?? false;
      const isArvSelected = selection.arv ?? false;

      const handleAivChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;
        setRowSelection((prev) => ({
          ...prev,
          [rowId]: {
            ...prev[rowId],
            listingData: row.original.listingData,
            aiv: checked,
            arv: checked ? false : (prev[rowId]?.arv ?? false),
          },
        }));
      };

      const handleArvChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;
        setRowSelection((prev) => ({
          ...prev,
          [rowId]: {
            ...prev[rowId],
            listingData: row.original.listingData,
            arv: checked,
            aiv: checked ? false : (prev[rowId]?.aiv ?? false),
          },
        }));
      };

      return (
        <Flex
          height="100%"
          minWidth={`${AIV_ARV_SELECT_COLUMN_SIZE}px`}
          alignItems="center"
          justifyContent="space-around"
          paddingX="8px"
          gap="8px"
          onClick={(e) => e.stopPropagation()}
        >
          <Flex flexDirection="column" alignItems="center" gap="2px">
            <Box sx={noFocusRingSx}>
              <Checkbox
                onChange={handleAivChange}
                isChecked={isAivSelected}
                aria-label={`AIV comp selection for ${rowId}`}
              />
            </Box>
          </Flex>
          <Flex flexDirection="column" alignItems="center" gap="2px">
            <Box sx={noFocusRingSx}>
              <Checkbox
                onChange={handleArvChange}
                isChecked={isArvSelected}
                aria-label={`ARV comp selection for ${rowId}`}
              />
            </Box>
          </Flex>
        </Flex>
      );
    },
  };
};
